import { Component, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ApiShivaService } from '@core/apis/api-shiva.service';
import { ScannedEquipmentManager } from '@core/components/scanned-equipment-list/scanned-equipment.manager';
import { EQP_LOCATIONS, EquipmentLocationEnum, FormMode } from '@core/constants';
import {
  IEquipment,
  IEquipmentModel,
  IGenericListOptions,
  IMetadataScannedEquipment,
  IMiniEquipment,
  IRequiredEquipment,
  IPrintLabelItem_new,
  IPrintLabelObject_new,
  ITinyEquipment,
  IWorkOrderItems,
} from '@core/interfaces';
import { SignalsService } from '@core/services/signals.service';
import { PromisesService } from '@core/services/promises.service';
import { UserService } from '@core/services/user.service';

import { EquipmentsMacAddrModalComponent } from '@views/equipments/equipments-mac-addr-modal.component';
import { EquipmentScanSearchService } from '@views/work-order-items/work-order-items-metadata-templates/equipment-scan-search.service';
import { BulkEquipmentScanService } from '@views/work-order-items/work-order-items-metadata-templates/bulk-equipment-scan.service';

interface IPoprovMetadata {
  model: IEquipmentModel;
  equipment_model_id: number;
  quantity: number;
  equipments: IMiniEquipment[];
  requested_equipments?: IRequiredEquipment[];
}

@Component({
  selector: 'app-poprov-metadata',
  templateUrl: './poprov-metadata.component.html',
  styleUrls: ['../work-order-items-detail-metadata.component.less']
})
export class PoprovMetadataComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('f', { static: true }) public f: NgForm;
  @ViewChild('serialNumberInput', { static: true }) public serialNumberInput?: ElementRef | undefined;

  @Input() public mode: FormMode = 'normal';
  @Input() public woi: IWorkOrderItems;
  @Input() public woiSave: Function; // woi save method to call on seq update

  public loading: boolean;
  public readonly locationOptions = EQP_LOCATIONS;

  public equipmentModel: IEquipmentModel;
  public equipmentModelFieldOptions: IGenericListOptions;
  public canEditEquipmentModel: boolean = false;
  public canEditQuantityField: boolean = false;

  public serialNumber: string;
  public allEquipmentScanned: boolean = false;
  public hasInvalidScannedEquipment: boolean = false;

  private metadataBackup: IPoprovMetadata;
  private woiEditionCancelledSignalHandler: Subscription;
  private poprovRefreshSignalHandler: Subscription;

  private readonly bulkEquipmentScanService = inject(BulkEquipmentScanService);

  constructor(
    private readonly ngbModal: NgbModal,
    private readonly apiShiva: ApiShivaService,
    private readonly signalsService: SignalsService,
    private readonly promisesService: PromisesService,
    private readonly userService: UserService,
    private readonly toastr: ToastrService,
    private readonly scannedEquipmentManager: ScannedEquipmentManager,
    private readonly equipmentScanSearchService: EquipmentScanSearchService,
  ) { }

  public ngOnInit(): void {
    this.woi.metadata = this.woi.metadata || {} as IPoprovMetadata;

    this.woiEditionCancelledSignalHandler = this.signalsService.subscribe('woi-edition-cancelled', () => {
      this.woi.metadata = { ...this.woi.metadata, ...this.metadataBackup };
      this._init();
    });

    this.poprovRefreshSignalHandler = this.signalsService.subscribe('poprov-metadata-refresh', () => this._init());

    // allow the buildPrintData method to be called from other components
    this.buildPrintData = this.buildPrintData.bind(this);

    this._init();
  }

  /**
   *  This function will be called for every input change, so the mode will trigger a change too,
   *   but we can't properly detect if the woi has changed because it's structure is too complex
   *  Handle the metadata update from the parent view (ex: 'cancel' action that does a backup)
   */
  public ngOnChanges(changes: SimpleChanges): void {
    const previousMode = changes?.mode?.previousValue;
    const currentMode = changes?.mode?.currentValue;

    if (previousMode === 'normal' && currentMode === 'edition') {
      this.metadataBackup =  JSON.parse(JSON.stringify(this.woi.metadata));
      setTimeout(() => this.serialNumberInput?.nativeElement.focus());
    }

    if (previousMode === 'edition' && currentMode === 'normal') {
      this._checkScannedEquipmentValidity();
    }
  }

  public ngOnDestroy(): void {
    this.woiEditionCancelledSignalHandler.unsubscribe();
    this.poprovRefreshSignalHandler.unsubscribe();
  }

  /**
   * Update the scanned equipment metadata with the updated list provided by the scanned equipment list component
   * @param updatedEquipmentMetadata The scanned equipment list
   */
  public updateScannedEquipmentMetadata(updatedEquipmentMetadata: IMetadataScannedEquipment[]): void {
    this.woi.metadata.equipments = [...updatedEquipmentMetadata];
    this._updateScannedEquipment();
  }

  /**
   * Return the equipment to the stock if it is marked as ignored in the scanned equipment list
   * @param equipmentCode The ignored equipment code
   */
  public async updateEquipmentLocation(equipmentCode: string): Promise<void> {
    const equipment: IEquipment = await this.apiShiva.equipments.detail(equipmentCode);
    if (equipment.location !== EquipmentLocationEnum.Collect) {
      return;
    }
    equipment.location = EquipmentLocationEnum.Stock;
    await this.apiShiva.equipments.update(equipment.id, equipment);
  }

  public hasPermissions(...permissions: string[]): boolean {
    return this.userService.hasPermissions(...permissions);
  }

  public async onChangeEquipmentModel(newVal: IEquipmentModel): Promise<void> {
    if (!newVal) {
      return;
    }
    this.woi.metadata.equipment_model_id = this.equipmentModel && this.equipmentModel.id;
  }

  public openBulkAdd(): void {
    this.loading = true;
    this.bulkEquipmentScanService.run({
      woi: this.woi,
      equipmentModel: this.equipmentModel,
      quantityToScan: this.woi.metadata.quantity || 0,
      requireMacAddress: !!this.equipmentModel?.need_mac_address,
      requireIpui: !!this.equipmentModel?.need_ipui,
      addEquipments: (equipments: IEquipment[]) => this._addScannedEquipments(equipments),
    }).finally(() => this.loading = false);
  }

  public openBulkRemove(): void {
    this.loading = true;
    this.bulkEquipmentScanService.runRemove(
      this.woi,
      this.woi.metadata.quantity || 0,
      (remainingEquipments: IMetadataScannedEquipment[]) => this._removeScannedEquipments(remainingEquipments),
    ).finally(() => this.loading = false);
  }

  public searchForSerial(): void {
    this.f.controls.serialNumber.markAsTouched();
    this.loading = true;
    this.equipmentScanSearchService.searchBySerial(this.serialNumber, this.woi.metadata.equipments || [])
      .then((equipment: IEquipment) => {
        this.serialNumber = null;
        return this._addEqpToList(equipment);
      })
      .catch((message: string | undefined) => {
        if (message) {
          this.toastr.error(message, 'Recherche Equipement');
        }
      })
      .finally(() => this.loading = false);
  }

  public buildPrintData(currentEqp: IMiniEquipment = null): Promise<IPrintLabelObject_new> {
    const deferred = this.promisesService.defer();

    const parentName = this.woi?.work_order?.entity?.parent?.name;
    const entityName = this.woi?.work_order?.entity?.name;
    const customerRef = this.woi?.work_order?.entity?.customer_ref;
    const locationCity = this.woi?.location?.city;

    const printData = {
      woi_code: this.woi.code || '',
      quantity: this.woi?.metadata?.quantity || 1 ,
      equipments_codes: [],
      labels_data: {
        entity_code: this.woi?.work_order?.entity?.code || '',
        parent_name: parentName || entityName || '',
        customer_ref: customerRef || locationCity || '',
        labels: this._getPrintDataEqpObj(currentEqp),
        lr_code: this.woi?.logistics_request?.code || '',
        company_name: this.woi.work_order?.entity?.parent?.name || '',
      }
    };
    deferred.resolve(printData);

    return deferred.promise;
  }

  private _init(): void {
    this.equipmentModelFieldOptions = {
      disabledButtons: {
        create: true
      }
    };

    this._fetchEquipmentModel(this.woi?.metadata?.equipment_model_id);

    this.canEditEquipmentModel = ['ready', 'in-progress'].includes(this.woi?.state?.name);
    this.canEditQuantityField = ['ready', 'in-progress'].includes(this.woi?.state?.name);

    if (['in-progress'].includes(this.woi?.state?.name)) {
      this._checkScannedEquipmentValidity();
    }

    // allow the buildPrintData method to be called from other components
    this.buildPrintData = this.buildPrintData.bind(this);
  }

  /**
   * Load the equipment model from the ID saved in the metadata.
   * If the ID is not provided, set the equipment model to undefined.
   * @param equipmentModelId The equipment model to loiad
   * @private
   */
  private _fetchEquipmentModel(equipmentModelId: number | undefined): void {
    if (!equipmentModelId) {
      this.equipmentModel = undefined;
      return;
    }

    this.apiShiva.equipment_models.detail(equipmentModelId)
      .then((res: IEquipmentModel) => this.equipmentModel = res)
      .catch((err: unknown) => {
        const errorMsg = `Impossible de récupérer les informations liées au modèle ${equipmentModelId}. Veuillez rafraîchir votre page.`;
        this.toastr.error(errorMsg);
        console.error(err);
      });
  }

  private _addEqpToList(equipment: ITinyEquipment): void {
    const needMacAddress = equipment.model.need_mac_address && !equipment.mac_address;
    const needIpui = equipment.model.need_ipui && !equipment.ipui;

    // checking if one or both metadata fields are required by the model
    if (needMacAddress || needIpui) {
      this._addEquipmentMetadata(equipment, needMacAddress, needIpui);
    } else {
      this._addScannedEquipment(equipment);
    }
  }

  private _addEquipmentMetadata(equipment: ITinyEquipment, needMacAddress: boolean, needIpui: boolean): void {
    const modal = this.ngbModal.open(EquipmentsMacAddrModalComponent, { backdrop: 'static', size: 'md' });
    modal.componentInstance.requiresMacAddress = needMacAddress;
    modal.componentInstance.requiresIpui = needIpui;
    modal.result.then(
      (res: { macAddress?: string, ipui?: string }) => {
        equipment.mac_address = res?.macAddress ? res.macAddress : equipment.mac_address;
        equipment.ipui = res?.ipui ? res.ipui : equipment.ipui;
        this._addScannedEquipment(equipment);
      },
      () => this.toastr.warning(`Les métadonnées requises n'ayant pas été renseignées, l'équipement n'a pas été ajouté.`),
    );
  }

  private _removeScannedEquipments(remainingEquipments: IMetadataScannedEquipment[]): void {
    this.woi.metadata.equipments = [...remainingEquipments];
    this._updateScannedEquipment();
  }

  private _addScannedEquipments(equipments: ITinyEquipment[]): void {
    equipments.forEach((equipment: ITinyEquipment) => this._addScannedEquipment(equipment, false));
    this._updateScannedEquipment();
  }

  private _addScannedEquipment(equipment: ITinyEquipment, persist: boolean = true): void {
    const newScannedEquipment: IMetadataScannedEquipment[] = this.scannedEquipmentManager.addScannedEquipment(
      equipment,
      this.woi.metadata.equipments,
      this.woi.metadata.requested_equipments,
      this.woi,
      this.equipmentModel,
    );

    // Add the scanned equipment metadata to the work order items metadata
    this.woi.metadata.equipments = [...newScannedEquipment];
    if (persist) {
      this._updateScannedEquipment();
    }
  }

  private _updateScannedEquipment(): void {

    // Update the view to reflect the new values
    this._checkScannedEquipmentValidity();

    // Make sure we update the work order item, in case the user refreshes the page or something
    this.woiSave();

    // Select the serial number input again for the next equipment
    setTimeout(() => this.serialNumberInput?.nativeElement.focus());
  }

  private _checkScannedEquipmentValidity(): void {
    const { allScanned, hasErrors } = this.scannedEquipmentManager.checkScanStatus(this.woi.metadata.quantity, this.woi.metadata.equipments);
    this.allEquipmentScanned = allScanned;
    this.hasInvalidScannedEquipment = hasErrors;
  }

  private _getPrintDataEqpObj(currentEqp: IMiniEquipment): IPrintLabelItem_new[] {
    const printLabelItem: IPrintLabelItem_new[] = [];
    const eqpList = this.woi?.metadata?.equipments;
    if (!currentEqp && eqpList && eqpList.length) {
      eqpList.forEach(() => {
        printLabelItem.push({
          equipment_name: '',
        });
      });
    } else {
      printLabelItem.push({
        equipment_name: '',
      });
    }

    return printLabelItem;
  }
}
